import React, { useMemo, useState } from 'react';
import { Button, Input, Tooltip } from 'antd';
import { X } from 'lucide-react';
import { HTS_CANONICAL_SECTIONS } from '@/components/HtsQuestionForm/htsQuestionFormOptions';
import type { HtsQuestionInput } from '@/services/api';
import styles from './HtsSectionsManager.module.scss';

interface SectionRow {
  name: string;
  count: number;
  isCanonical: boolean;
}

interface HtsSectionsManagerProps {
  questions: HtsQuestionInput[];
  onChange: (next: HtsQuestionInput[]) => void;
  extraSections: string[];
  onAddExtraSection: (name: string) => void;
  onRemoveExtraSection: (name: string) => void;
  onRenameExtraSection: (oldName: string, newName: string) => void;
}

export const HtsSectionsManager: React.FC<HtsSectionsManagerProps> = ({
  questions,
  onChange,
  extraSections,
  onAddExtraSection,
  onRemoveExtraSection,
  onRenameExtraSection,
}) => {
  const [newSectionValue, setNewSectionValue] = useState('');

  const sections = useMemo<SectionRow[]>(() => {
    const counts = new Map<string, number>();
    questions.forEach((q) => {
      const name = q.section.trim() || 'No section';
      counts.set(name, (counts.get(name) ?? 0) + 1);
    });

    const seen = new Set<string>();
    const rows: SectionRow[] = [];
    const pushRow = (name: string) => {
      if (seen.has(name)) return;
      seen.add(name);
      rows.push({ name, count: counts.get(name) ?? 0, isCanonical: HTS_CANONICAL_SECTIONS.includes(name) });
    };

    HTS_CANONICAL_SECTIONS.forEach(pushRow);
    questions.forEach((q) => pushRow(q.section.trim() || 'No section'));
    extraSections.forEach(pushRow);

    return rows;
  }, [questions, extraSections]);

  const renameSection = (row: SectionRow, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === row.name || row.isCanonical) return;
    if (row.count === 0) {
      onRenameExtraSection(row.name, trimmed);
      return;
    }
    onChange(
      questions.map((q) => ((q.section.trim() || 'No section') === row.name ? { ...q, section: trimmed } : q))
    );
  };

  const addSection = () => {
    const trimmed = newSectionValue.trim();
    if (!trimmed) return;
    onAddExtraSection(trimmed);
    setNewSectionValue('');
  };

  return (
    <div className={styles.panel}>
      <div className={styles.title}>Manage sections</div>
      <div className={styles.rows}>
        {sections.map((row) => (
          <div className={styles.row} key={row.name}>
            <Input
              defaultValue={row.name}
              disabled={row.isCanonical}
              onBlur={(e) => renameSection(row, e.target.value)}
              onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
              className={styles.rowInput}
            />
            <Tooltip
              title={
                row.isCanonical
                  ? 'Default section — always available'
                  : row.count > 0
                    ? `${row.count} question${row.count === 1 ? '' : 's'} using this section`
                    : 'Remove this section'
              }
            >
              <button
                type="button"
                className={styles.rowDelete}
                disabled={row.isCanonical || row.count > 0}
                onClick={() => onRemoveExtraSection(row.name)}
              >
                <X style={{ width: '1em', height: '1em' }} />
              </button>
            </Tooltip>
          </div>
        ))}
      </div>

      <div className={styles.addRow}>
        <Input
          placeholder="Type a new section name"
          value={newSectionValue}
          onChange={(e) => setNewSectionValue(e.target.value)}
          onPressEnter={addSection}
          className={styles.rowInput}
        />
        <Button onClick={addSection} disabled={!newSectionValue.trim()}>
          + Add
        </Button>
      </div>
    </div>
  );
};

export default HtsSectionsManager;
